/**
 * WebSocket envelope, client ops and per-event payload schemas (OpenAPI `Ws*` components).
 * The discriminated union of all server events lives in `../ws.ts`.
 */
import { z } from 'zod';
import {
  ActorRef,
  CoverMode,
  ErrorResponse,
  Id,
  JsonObject,
  Percent,
  Timestamp,
  VersionState,
} from './common.js';
import { AssetCard } from './assets.js';
import { Board, BoardCover } from './boards.js';
import { Comment } from './comments.js';
import { AssetVersion } from './versions.js';
import { Workspace } from './workspaces.js';
import { Member } from './members.js';
import { CustomField } from './customFields.js';
import { Tag } from './tags.js';

export const WS_ROOM_KINDS = ['workspace', 'board', 'asset'] as const;
export type WsRoomKind = (typeof WS_ROOM_KINDS)[number];

export const WS_ROOM_PATTERN = /^(workspace|board|asset):[0-9a-fA-F-]{36}$/;

export const WsRoom = z.string().regex(WS_ROOM_PATTERN);
export type WsRoom = z.infer<typeof WsRoom>;

/** Every server→client event. `id` is a Redis-stream id usable as `last_event_id`. */
export const WsEventEnvelope = z.object({
  id: z.string(),
  ts: Timestamp,
  room: WsRoom,
  type: z.string(),
  actor: ActorRef,
  payload: JsonObject,
});
export type WsEventEnvelope = z.infer<typeof WsEventEnvelope>;

export const WsClientOp = z.enum(['subscribe', 'unsubscribe', 'presence.heartbeat', 'ping']);
export type WsClientOp = z.infer<typeof WsClientOp>;

export const WsPresence = z.object({
  room: z.string().optional(),
  playhead_ms: z.int().nullable().optional(),
  version_id: Id.nullable().optional(),
});
export type WsPresence = z.infer<typeof WsPresence>;

/** Client→server messages (rate-limited 60/min per connection). */
export const WsClientMessage = z.object({
  op: WsClientOp,
  rooms: z.array(WsRoom).optional(),
  last_event_id: z.string().nullable().optional(),
  presence: WsPresence.optional(),
  req_id: z.string().optional(),
});
export type WsClientMessage = z.infer<typeof WsClientMessage>;

export const WsAckOp = z.enum(['ack', 'error', 'pong']);
export type WsAckOp = z.infer<typeof WsAckOp>;

export const WsAck = z.object({
  op: WsAckOp,
  req_id: z.string().nullable(),
  ok: z.boolean(),
  rooms: z.array(z.string()).optional(),
  replayed: z.int().optional(),
  replay_truncated: z.boolean().optional(),
  error: ErrorResponse.optional(),
});
export type WsAck = z.infer<typeof WsAck>;

export const WsAssetCreatedPayload = z.object({
  board_id: Id,
  asset: AssetCard,
  client_upload_id: Id.nullable(),
  position: z.enum(['top', 'bottom']).optional(),
});
export type WsAssetCreatedPayload = z.infer<typeof WsAssetCreatedPayload>;

export const WsAssetChangedField = z.enum([
  'title',
  'description',
  'tags',
  'custom_fields',
  'approval_status',
  'active_version',
  'dominant_colors',
  'ai_tags',
  'ocr_text',
  'comment_count',
  'archived_at',
  'boards',
  // release2
  'rights',
  'incomplete',
  // release3
  'share_excluded',
]);
export type WsAssetChangedField = z.infer<typeof WsAssetChangedField>;

export const WsAssetUpdatedPayload = z.object({
  asset_id: Id,
  changed: z.array(WsAssetChangedField),
  asset: AssetCard,
});
export type WsAssetUpdatedPayload = z.infer<typeof WsAssetUpdatedPayload>;

export const WsAssetRemovedReason = z.enum(['removed', 'moved', 'deleted', 'upload_aborted']);
export type WsAssetRemovedReason = z.infer<typeof WsAssetRemovedReason>;

export const WsAssetRemovedPayload = z.object({
  board_id: Id,
  asset_id: Id,
  reason: WsAssetRemovedReason,
  moved_to_board_id: Id.nullable().optional(),
});
export type WsAssetRemovedPayload = z.infer<typeof WsAssetRemovedPayload>;

export const WsAssetMovedPayload = z.object({
  board_id: Id,
  asset_id: Id,
  from_board_id: Id.nullable().optional(),
  sort_order: z.int().nullable().optional(),
});
export type WsAssetMovedPayload = z.infer<typeof WsAssetMovedPayload>;

export const WsAssetProcessingPayload = z.object({
  asset_id: Id,
  version_id: Id,
  state: VersionState,
  progress: Percent.nullable(),
  card: AssetCard.optional(),
});
export type WsAssetProcessingPayload = z.infer<typeof WsAssetProcessingPayload>;

export const WsBoardPayload = z.object({
  board: Board,
  parent_board_id: Id.nullable().optional(),
  previous_parent_board_id: Id.nullable().optional(),
});
export type WsBoardPayload = z.infer<typeof WsBoardPayload>;

export const WsBoardDeletedPayload = z.object({
  board_id: Id,
  parent_board_id: Id.nullable(),
  deleted_subtree_ids: z.array(Id),
});
export type WsBoardDeletedPayload = z.infer<typeof WsBoardDeletedPayload>;

export const WsCoverChangedPayload = z.object({
  board_id: Id,
  cover_mode: CoverMode,
  cover: BoardCover,
});
export type WsCoverChangedPayload = z.infer<typeof WsCoverChangedPayload>;

export const WsCommentPayload = z.object({
  asset_id: Id,
  version_id: Id,
  comment: Comment,
  temp_id: z.string().nullable().optional(),
});
export type WsCommentPayload = z.infer<typeof WsCommentPayload>;

export const WsCommentDeletedPayload = z.object({
  asset_id: Id,
  comment_id: Id,
  parent_comment_id: Id.nullable(),
});
export type WsCommentDeletedPayload = z.infer<typeof WsCommentDeletedPayload>;

export const WsVersionPayload = z.object({
  asset_id: Id,
  version: AssetVersion,
  previous_active_version_id: Id.nullable().optional(),
  client_upload_id: Id.nullable().optional(),
});
export type WsVersionPayload = z.infer<typeof WsVersionPayload>;

export const WsVersionStateChangedPayload = z.object({
  asset_id: Id,
  version_id: Id,
  state: VersionState,
  progress: Percent.nullable().optional(),
  step: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  retryable: z.boolean().nullable().optional(),
  version: AssetVersion.optional(),
});
export type WsVersionStateChangedPayload = z.infer<typeof WsVersionStateChangedPayload>;

export const WsUploadPayload = z.object({
  upload_id: Id,
  client_upload_id: Id.nullable().optional(),
  asset_id: Id,
  version_id: Id,
  board_id: Id,
  asset_deleted: z.boolean().optional(),
});
export type WsUploadPayload = z.infer<typeof WsUploadPayload>;

export const WsPresenceViewer = z.object({
  connection_id: z.string(),
  actor: ActorRef,
  playhead_ms: z.int().nullable().optional(),
  version_id: Id.nullable().optional(),
  last_seen_at: Timestamp,
});
export type WsPresenceViewer = z.infer<typeof WsPresenceViewer>;

export const WsPresencePayload = z.object({
  room: z.string(),
  viewers: z.array(WsPresenceViewer),
});
export type WsPresencePayload = z.infer<typeof WsPresencePayload>;

export const WsWorkspacePayload = z.object({
  workspace_id: Id,
  workspace: Workspace.optional(),
  member: Member.optional(),
  member_id: Id.nullable().optional(),
  custom_field: CustomField.optional(),
  tag: Tag.optional(),
  change: z.enum(['created', 'updated', 'deleted']).optional(),
  permissions_changed: z.boolean().optional(),
});
export type WsWorkspacePayload = z.infer<typeof WsWorkspacePayload>;
